/**
 * specialtyStarterPacks.js (demo-only)
 * ──────────────────────────────────────────────────────────────────────
 * One-click "starter packs" per specialty. Each pack provides a sensible
 * default note format + tuned section list + per-section AI prompt hints
 * so a new provider in that specialty has working defaults from minute 1.
 *
 * Selecting a pack from the configurator REPLACES the current global
 * config (or per-visit-type config). It does not auto-apply on specialty
 * change — the configurator surfaces a dismissible banner and the user
 * confirms.
 *
 * Pack shape mirrors what `buildDefaultConfig` returns:
 *   { formatId, sections: [...], copyForward: { sectionsEnabled, rules,
 *     conditions }, assessments: { seeds } }
 */

const sec = (id, label, opts = {}) => ({
  id,
  label,
  enabled: true,
  promptHint: opts.promptHint || '',
  subheadings: (opts.subheadings || []).map((s, i) => (
    typeof s === 'string' ? { id: `${id}-sub-${i}`, label: s } : s
  )),
});

const buildPack = (formatId, sections, opts = {}) => {
  const sectionsEnabled = {};
  const rules = {};
  const ordered = sections.map((s, i) => ({ ...s, order: i }));
  for (const s of ordered) {
    sectionsEnabled[s.id] = true;
    rules[s.id] = opts.rules?.[s.id] || 'same-visit';
  }
  return {
    formatId,
    sections: ordered,
    copyForward: { sectionsEnabled, rules, conditions: opts.conditions || {} },
    assessments: { seeds: opts.seeds || [{ id: 'a-1', label: 'A/P — Primary' }] },
  };
};

/* Internal Medicine — broad SOAP w/ care-gap prompts. */
const PACK_INTERNAL_MEDICINE = buildPack('soap', [
  sec('subjective', 'Subjective', {
    promptHint: 'Capture HPI in OLDCARTS format. Surface chronic conditions due for review.',
    subheadings: ['Chief Complaint', 'HPI', 'ROS'],
  }),
  sec('objective', 'Objective', {
    promptHint: 'Pull last vitals and pertinent labs from prior 90 days.',
    subheadings: ['Vitals', 'Physical Exam', 'Labs / Imaging'],
  }),
  sec('assessment', 'Assessment', {
    promptHint: 'List active problems with ICD-10 + brief stability comment per diagnosis.',
  }),
  sec('plan', 'Plan', {
    promptHint: 'Per problem: action + follow-up interval. Flag overdue screening (lipids, A1c, colon).',
  }),
]);

/* Cardiology — Consult Report with cardiac-exam hints. */
const PACK_CARDIOLOGY = buildPack('consult', [
  sec('chiefComplaint', 'Reason for Consultation', { promptHint: 'State referring physician + specific clinical question.' }),
  sec('hpi', 'History of Present Illness', { promptHint: 'Always include chest-pain SOCRATES if present; capture functional class.' }),
  sec('cardHx', 'Cardiac History', { promptHint: 'Prior MI, CABG/PCI, EF, devices, arrhythmias, prior stress tests.' }),
  sec('medications', 'Medications', { promptHint: 'Highlight beta-blocker, ACEi/ARB, statin, antiplatelet — note adherence.' }),
  sec('vitals', 'Vitals', { promptHint: 'BP both arms if first visit; resting HR; orthostatics if syncope work-up.' }),
  sec('physical', 'Cardiac Exam', {
    promptHint: 'Document JVP, PMI, S1/S2, murmurs (grade/location/radiation), peripheral pulses.',
    subheadings: ['Auscultation', 'JVP', 'Peripheral Pulses', 'Edema'],
  }),
  sec('labsImaging', 'Labs / Imaging Reviewed', { promptHint: 'EKG (rate/rhythm/intervals), echo (EF, valves), stress test, BNP, troponin.' }),
  sec('assessment', 'Impression', { promptHint: 'Summarize cardiac risk; stratify ASCVD if not already.' }),
  sec('plan', 'Recommendations / Plan', { promptHint: 'Med adjustments, work-up, lifestyle, follow-up cadence.' }),
], {
  rules: { medications: 'always', cardHx: 'always' },
  seeds: [{ id: 'a-1', label: 'A/P — Cardiac' }, { id: 'a-2', label: 'A/P — Risk factor mgmt' }],
});

/* Pediatrics — SOAP with growth + anticipatory guidance. */
const PACK_PEDIATRICS = buildPack('soap', [
  sec('subjective', 'Subjective', {
    promptHint: 'Feeding, sleep, milestones, behavior. Caregiver concerns. Time off school.',
    subheadings: ['Feeding', 'Sleep', 'Development', 'Behavior'],
  }),
  sec('objective', 'Objective', {
    promptHint: 'Growth percentiles (weight/length/head circ). Tanner stage if applicable.',
    subheadings: ['Vitals + Growth', 'Physical Exam'],
  }),
  sec('immunization', 'Immunization', { promptHint: 'Reconcile to ACIP catch-up schedule. Flag overdue.' }),
  sec('assessment', 'Assessment', { promptHint: 'Age-appropriate well or sick assessment; flag developmental concerns.' }),
  sec('plan', 'Plan / Anticipatory Guidance', { promptHint: 'Safety, nutrition, screen-time, next-visit interval. Caregiver questions answered.' }),
], {
  rules: { immunization: 'always', subjective: 'same-visit' },
  seeds: [{ id: 'a-1', label: 'A/P — Well child' }],
});

/* Psychiatry / Behavioral Health — BIRP with risk + therapy hints. */
const PACK_PSYCHIATRY = buildPack('birp', [
  sec('behavior', 'Behavior', { promptHint: 'MSE: appearance, mood/affect, thought process/content, cognition. SI/HI screen.' }),
  sec('intervention', 'Intervention', { promptHint: 'Therapeutic technique used (CBT, DBT, MI, supportive). Med review.' }),
  sec('response', 'Response', { promptHint: 'Patient response, insight, engagement. PHQ-9/GAD-7/AUDIT-C if due.' }),
  sec('plan', 'Plan', { promptHint: 'Medication changes, therapy frequency, safety plan if any risk noted.' }),
], {
  rules: { behavior: 'never' },
  seeds: [{ id: 'a-1', label: 'A/P — Mood' }, { id: 'a-2', label: 'A/P — Anxiety' }],
});

/* Surgery (general) — H&P focus. */
const PACK_SURGERY = buildPack('hp', [
  sec('chiefComplaint', 'Chief Complaint', { promptHint: 'Indication for surgical evaluation in 1 line.' }),
  sec('hpi', 'HPI', { promptHint: 'Onset, progression, prior treatments, imaging done. Symptoms suggesting urgency.' }),
  sec('pastMedHx', 'Past Medical History', { promptHint: 'Cardiac, pulm, bleeding/clotting, anesthesia history.' }),
  sec('medications', 'Medications', { promptHint: 'Anticoagulants (note last dose), antiplatelets, NSAIDs, hormonal.' }),
  sec('allergies', 'Allergies'),
  sec('socialHx', 'Social History', { promptHint: 'Tobacco/EtOH/drug use — quantify; impact on perioperative risk.' }),
  sec('vitals', 'Vitals'),
  sec('physical', 'Physical Exam', { promptHint: 'Focused exam of involved system + airway/cardio for anesthesia clearance.' }),
  sec('assessment', 'Assessment', { promptHint: 'Surgical diagnosis + ASA class.' }),
  sec('plan', 'Plan', { promptHint: 'Pre-op work-up, consents, scheduling, NPO instructions, optimization.' }),
], {
  rules: { medications: 'always', allergies: 'always', pastMedHx: 'always' },
  seeds: [{ id: 'a-1', label: 'A/P — Surgical' }, { id: 'a-2', label: 'A/P — Pre-op optimization' }],
});

/* Urgent Care — DAP for fast turnaround. */
const PACK_URGENT_CARE = buildPack('dap', [
  sec('data', 'Data', {
    promptHint: 'Brief HPI, focused exam, point-of-care results.',
    subheadings: ['Subjective', 'Objective'],
  }),
  sec('assessment', 'Assessment', { promptHint: 'Acute diagnosis + severity. Flag red flags warranting ED transfer.' }),
  sec('plan', 'Plan', { promptHint: 'Treatment, prescriptions (with dose/duration), return precautions, follow-up.' }),
], {
  seeds: [{ id: 'a-1', label: 'A/P — Acute' }],
});

export const SPECIALTY_STARTER_PACKS = {
  internal_medicine: { label: 'Internal Medicine', pack: PACK_INTERNAL_MEDICINE },
  family_medicine:   { label: 'Family Medicine',   pack: PACK_INTERNAL_MEDICINE },
  cardiology:        { label: 'Cardiology',        pack: PACK_CARDIOLOGY },
  pediatrics:        { label: 'Pediatrics',        pack: PACK_PEDIATRICS },
  psychiatry:        { label: 'Psychiatry',        pack: PACK_PSYCHIATRY },
  general_surgery:   { label: 'General Surgery',   pack: PACK_SURGERY },
  urgent_care:       { label: 'Urgent Care',       pack: PACK_URGENT_CARE },
};

export const getStarterPackForSpecialty = (specialtyId) => SPECIALTY_STARTER_PACKS[specialtyId] || null;

/* Deep-clone helper so the packs themselves are never mutated. */
export const cloneStarterPack = (pack) => ({
  formatId: pack.formatId,
  sections: pack.sections.map((s, i) => ({
    ...s,
    order: i,
    subheadings: (s.subheadings || []).map((x) => ({ ...x })),
  })),
  copyForward: {
    sectionsEnabled: { ...(pack.copyForward.sectionsEnabled || {}) },
    rules: { ...(pack.copyForward.rules || {}) },
    conditions: { ...(pack.copyForward.conditions || {}) },
  },
  assessments: { seeds: (pack.assessments.seeds || []).map((s) => ({ ...s })) },
});
